if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      setupFocusSentinels() {
        const dialog = this.querySelector('[role="dialog"]');
        if (!dialog) return;

        const getFocusableInDialog = () => getFocusableElements(dialog).filter((el) => !el.hasAttribute('data-focus-sentinel'));
        const focusFirst = () => {
          const focusables = getFocusableInDialog();
          (focusables[0] || dialog).focus();
        };
        const focusLast = () => {
          const focusables = getFocusableInDialog();
          (focusables[focusables.length - 1] || dialog).focus();
        };

        if (!this._focusStartSentinel) {
          this._focusStartSentinel = document.createElement('button');
          this._focusStartSentinel.type = 'button';
          this._focusStartSentinel.setAttribute('aria-hidden', 'true');
          this._focusStartSentinel.setAttribute('tabindex', '0');
          this._focusStartSentinel.setAttribute('data-focus-sentinel', 'start');
          this._focusStartSentinel.className = 'focus-sentinel';
          this._focusStartSentinel.addEventListener('focus', focusLast);
          this.insertBefore(this._focusStartSentinel, this.firstChild);
        }

        if (!this._focusEndSentinel) {
          this._focusEndSentinel = document.createElement('button');
          this._focusEndSentinel.type = 'button';
          this._focusEndSentinel.setAttribute('aria-hidden', 'true');
          this._focusEndSentinel.setAttribute('tabindex', '0');
          this._focusEndSentinel.setAttribute('data-focus-sentinel', 'end');
          this._focusEndSentinel.className = 'focus-sentinel';
          this._focusEndSentinel.addEventListener('focus', focusFirst);
          this.appendChild(this._focusEndSentinel);
        }
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        window.pauseAllMedia();
        
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.initializeXRButtons();

            super.show(opener);
            this.setupFocusSentinels();
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());

        // Keep breadcrumbs visible in quick view when they are configured
        // in sidebar position (inside .product-navigation.quick-add-hidden).
        const productInfoContainer = productElement.querySelector('.product__info-container');
        const sidebarNavigation = productElement.querySelector('.product-navigation.quick-add-hidden');
        const sidebarBreadcrumbs = sidebarNavigation?.querySelector('.breadcrumbs');
        if (sidebarBreadcrumbs && productInfoContainer) {
          if (!productInfoContainer.querySelector('.breadcrumbs')) {
            const breadcrumbsClone = sidebarBreadcrumbs.cloneNode(true);
            productInfoContainer.insertBefore(breadcrumbsClone, productInfoContainer.firstChild);
          }

          // Fallback: keep navigation block itself with breadcrumbs only,
          // so it survives the quick-add-hidden cleanup.
          sidebarNavigation.classList.remove('quick-add-hidden');
          Array.from(sidebarNavigation.children).forEach((child) => {
            if (!child.classList.contains('breadcrumbs')) child.remove();
          });
        }

        const hiddenElements = productElement.querySelectorAll('.quick-add-hidden');
        if (hiddenElements) hiddenElements.forEach((hidden) => hidden.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }

      initializeXRButtons() {
        setTimeout(() => {
          const xrButtons = this.modalContent.querySelectorAll('[data-shopify-xr]');
          
          if (xrButtons.length > 0) {
            
            let productJSON = this.modalContent.querySelector('[id^="ProductJSON-"]');
            
            if (!productJSON) {
              const mainProductJSON = document.querySelector('[id^="ProductJSON-"]');
              if (mainProductJSON) {
                productJSON = mainProductJSON.cloneNode(true);
                const originalId = productJSON.id;
                productJSON.id = `quickadd-${originalId}`;
                this.modalContent.appendChild(productJSON);
              }
            }
            
            if (productJSON && window.ShopifyXR) {
              try {
                const modelData = JSON.parse(productJSON.textContent);
                window.ShopifyXR.addModels(modelData);
                window.ShopifyXR.setupXRElements();
              } catch (error) {
                console.error('Error processing model data:', error);
              }
            } else if (window.ProductModel) {
              window.ProductModel.loadShopifyXR();
            }
            
            xrButtons.forEach(button => {
              if (window.ShopifyXR) {
                button.addEventListener('click', (e) => {
                  e.preventDefault();
                  if (window.ShopifyXR) {
                    window.ShopifyXR.launch();
                  }
                });
              }
            });
          }
          
          this.updateSwiperInstances();
        }, 200);
      }

      updateSwiperInstances() {
        const mediaGallerySlider = this.modalContent.querySelector('media-gallery-slider');
        if (mediaGallerySlider && typeof mediaGallerySlider.updateSwiperInstances === 'function') {
          mediaGallerySlider.updateSwiperInstances();
        }

        const swiperElements = this.modalContent.querySelectorAll('.swiper');
        
        setTimeout(() => {
          swiperElements.forEach(swiperEl => {
            if (swiperEl.swiper && typeof swiperEl.swiper.update === 'function') {
              swiperEl.swiper.update();
            }
          });

          window.dispatchEvent(new Event('resize'));
        }, 200);
      }
    }
  );
}
